/**
 * Exceptions. What an administrator should look at for a period:
 *
 *  - every entry that took stock below zero, and every balance still below
 *    zero at the end of the period;
 *  - entries outside tolerance, with the reason given or the lack of one;
 *  - entries edited or deleted after they were made, with who and why.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { controls, type ExceptionsReport as Report } from "../../lib/controls";
import { fmtDate, fmtPct, fmtQty, fmtShift, fmtStatus, relativeTime } from "../../lib/format";
import { comparisonLabel, describePeriod } from "../../lib/period";
import { useCompare, useReportPeriod } from "../../lib/reportPeriod";
import { ReportDownload } from "../../components/ReportDownload";
import { PeriodPicker } from "../../components/PeriodPicker";
import { useDrill } from "../../components/Drill";
import { Cards } from "./ProductionReport";

export function ExceptionsReport() {
  const [period, setPeriod] = useReportPeriod();
  const [compare, setCompare] = useCompare();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);
  const open = useDrill();

  const load = useCallback(() => {
    setReport(null);
    setError(null);
    controls
      .exceptions(period.from, period.to, period.shift, compare)
      .then(setReport)
      .catch((e: ApiError) => setError(e.message));
  }, [period, compare]);

  useEffect(load, [load]);

  const entry = (runId: number | null, label: string) =>
    runId
      ? () => open({ source: "entry", group: [], filters: { run_id: runId }, crumbs: [label] })
      : undefined;

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Exceptions</h2>
          <p className="page__sub">{describePeriod(period)} · for the people who answer for the numbers</p>
        </div>
        <div className="page__controls">
          <PeriodPicker value={period} onChange={setPeriod} shifts
                        compare={compare} onCompare={setCompare} />
          <ReportDownload title="Exceptions" period={period} />
        </div>
      </header>

      {error && <div className="banner banner--negative">{error}</div>}
      {!report && !error && <div className="skeleton state__bar" style={{ height: 160 }} />}

      {report && (
        <>
          <Cards cards={report.cards} vs={comparisonLabel(period, compare)} />

          <section className="card">
            <h3 className="section-title">Negative stock at entry</h3>
            <p className="section-note">
              Allowed with a warning to whoever entered it. Editing or deleting the entry takes it off this list.
            </p>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>Material</th><th>Location</th>
                    <th className="num">Available</th><th className="num">Taken</th>
                    <th className="num">Short by</th><th>Entered by</th><th>Document</th>
                  </tr>
                </thead>
                <tbody>
                  {report.negative_events.map((e, i) => (
                    <tr key={i} className={e.run_id ? "rowlink" : undefined}
                        onClick={entry(e.run_id, e.doc_ref ?? "Entry")}>
                      <td>{fmtDate(e.business_date)}</td>
                      <td>{e.item} · {e.item_code}</td>
                      <td>{e.location}</td>
                      <td className="num mono">{fmtQty(e.available, 2)}</td>
                      <td className="num mono">{fmtQty(e.requested, 2)}</td>
                      <td className="num mono cell--negative">{fmtQty(e.short_by, 2)}</td>
                      <td>{e.entered_by ?? "—"}</td>
                      <td>{e.doc_ref ?? "—"}</td>
                    </tr>
                  ))}
                  {report.negative_events.length === 0 && (
                    <tr><td colSpan={8}>No entry took stock below zero in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <h3 className="section-title">Balances below zero on {fmtDate(report.period_to)}</h3>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr><th>Location</th><th>Material</th><th className="num">Balance</th></tr>
                </thead>
                <tbody>
                  {report.negative_now.map((r) => (
                    <tr key={`${r.location_code}-${r.item_code}`} className="rowlink"
                        onClick={() => open(r.drill)}>
                      <td>{r.location}</td>
                      <td>{r.item} · {r.item_code}</td>
                      <td className="num mono cell--negative">{fmtQty(r.qty, 2)} {r.uom.toLowerCase()}</td>
                    </tr>
                  ))}
                  {report.negative_now.length === 0 && (
                    <tr><td colSpan={3}>Every balance is zero or above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <h3 className="section-title">Outside tolerance</h3>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th><th>Shift</th><th>Machine</th>
                    <th className="num">Unaccounted</th><th className="num">Tolerance</th>
                    <th>Reason</th><th>Note</th><th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.out_of_tolerance.map((r) => (
                    <tr key={r.run_id} className="rowlink"
                        onClick={entry(r.run_id, `${r.machine} · ${r.business_date}`)}>
                      <td>{fmtDate(r.business_date)}</td>
                      <td>{fmtShift(r.shift)}</td>
                      <td>{r.machine}</td>
                      <td className="num mono">{fmtQty(r.variance_qty, 2)} kg · {fmtPct(r.variance_pct)}</td>
                      <td className="num mono">{fmtPct(r.tolerance_pct)}</td>
                      <td>{r.reason ?? <span className="pill pill--warning">no reason</span>}</td>
                      <td>{r.note ?? ""}</td>
                      <td>{fmtStatus(r.status)}</td>
                    </tr>
                  ))}
                  {report.out_of_tolerance.length === 0 && (
                    <tr><td colSpan={8}>Every entry was inside its machine's tolerance.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="card">
            <h3 className="section-title">Edited or deleted</h3>
            <div className="tablewrap">
              <table className="table">
                <thead>
                  <tr><th>When</th><th>Change</th><th>By</th><th>Entry</th><th>Why</th></tr>
                </thead>
                <tbody>
                  {report.changes.map((c, i) => (
                    <tr key={i} className="rowlink"
                        onClick={entry(c.run_id, `${c.machine ?? "Entry"} · ${c.business_date ?? ""}`)}>
                      <td>{relativeTime(c.at)}</td>
                      <td>{c.action === "DELETE" ? "Deleted" : "Edited"}</td>
                      <td>{c.user ?? "—"}</td>
                      <td>{c.machine ?? "—"} · {fmtDate(c.business_date)} · {fmtShift(c.shift)}</td>
                      <td>{c.reason ?? "—"}</td>
                    </tr>
                  ))}
                  {report.changes.length === 0 && (
                    <tr><td colSpan={5}>No entry in this period was changed after it was made.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
